<template>
  <div class="card-body py-3 text-left" v-if="!isLoaded || !isEmpty(organisation)">
    <h3 class="mb-3 t-500 t-md">
      General information
    </h3>

    <template v-if="isLoaded">
      <template v-if="organisation.organisation_types || relatedOrganisations.length > 0">
        <span v-if="!spinOffParent && organisation.organisation_types" class="t-primary t-lg t-600 mb-2">
          {{ industryAndTypeString }}
        </span>

        <related-organisations v-for="(relation, i) in relatedOrganisations"
          :relation="relation"
          :key="i"
        />
      </template>

      <template v-if="isInvestor">
        <template v-if="organisation.foundation_date || (investorFundsData && investorFundsData.fund_size)">
          <hr />
          <div class="mb-1 row" v-if="organisation.foundation_date">
            <span class="t-primary t-lg col-7 t-600">Founded <i v-if="isAI" class="fa fa-info-circle t-secondary t-sm pl-1" v-tooltip="`This founding date was inferred with AI`"></i></span>
            <span class="col-5 t-400 t-grey t-lg">{{foundationYear}}</span>
          </div>
          <div class="mb-1 row" v-if="investorFundsData && investorFundsData.fund_size">
            <span class="t-primary t-lg col-7 t-600">Fund size</span>
            <span class="col-5 t-400 t-grey t-lg">${{investorFundsData.fund_size | formatNumber}}</span>
          </div>
        </template>
      </template>
      <template v-else-if="organisation.foundation_date || organisation.company_size || !isEmpty(organisation.funding)">
        <hr />
        <!-- <div class="mb-1 row" v-if="organisation.out_of_business">
          <div class="col-12">
            <span
              class="label-base bg-red"
              v-tooltip="'Our sources indicate this company is out of business.'"
            >CLOSED</span>
          </div>
        </div> -->
        <div class="mb-1 row" v-if="organisation.foundation_date">
          <span class="t-primary t-lg col-7 t-600">Founded <i v-if="isAI" class="fa fa-info-circle t-secondary t-sm pl-1" v-tooltip="`This founding date was inferred with AI`"></i></span>
          <span class="col-5 t-400 t-grey t-lg">{{foundationYear}}</span>
        </div>
        <div class="mb-1 row" v-if="organisation.company_size">
          <span class="t-primary t-lg col-7 t-600">Est. employees</span>
          <span class="col-5 t-400 t-grey t-lg">{{organisation.company_size}}</span>
        </div>
        <template v-if="!isEmpty(organisation.funding)">
          <div class="mb-1 row">
            <span class="t-primary t-lg col-7 t-600">Funding stage</span>
            <span class="col-5 t-400 t-grey t-lg">
              {{organisation.funding.stage}} {{organisation.funding.year ? `(${organisation.funding.year})` : ''}}
            </span>
          </div>
          <div class="mb-1 row">
            <span class="t-primary t-lg col-7 t-600">Latest funding</span>
            <span class="col-5 t-400 t-grey t-lg">
              <template v-if="organisation.funding.amount && organisation.funding.amount.amount > 0">
                {{ { amount: organisation.funding.amount.amount, currency: organisation.funding.amount.currency } | formatCurrency }}
              </template>
              <template v-else>N/A</template>
            </span>
          </div>
        </template>
      </template>
      <!-- principal leadership(s) -->
      <template v-if="!isEmpty(principalLeaders) || locationString || country">
        <hr />
        <div v-for="leader in principalLeaders" :key="leader.full_name" class="mb-2">
          <p v-if="getLeadershipTitle(leader)" class="t-600 t-primary mb-0 t-lg">{{ getLeadershipTitle(leader) }}</p>
          <div class="d-flex justify-content-between align-items-center">
            <span><i class="t-lr fa fa-user t-secondary t-sm width--20px mr-1" />
              <router-link
                v-if="leader.researcher && !isEmpty(leader.researcher.slug_values)"
                :to="{
                  name: 'researcher.show',
                  params: { id: leader.researcher.slug_values[0]},
                  query: { fullName: stripTags(leader.full_name) }
                }">{{leader.full_name}}</router-link>
              <span v-else>{{leader.full_name}}</span>
            </span>
            <a v-if="leader.linked_in" :href="leader.linked_in" target="_blank">
              <i class="fa fa-linkedin t-secondary t-sm is-hover--blue" />
            </a>
          </div>
        </div>

        <!-- location -->
        <div v-if="locationString || country">
          <div class="t-600 t-primary mb-0 t-lg">Location</div>
          <span v-if="locationString"><i class="fa fa-map-marker width--20px mr-1 t-secondary t-sm" />{{ locationString }}</span>
          <span class="t-xxs position-absolute pl-2" v-if="country"> <flag :title="renderCountryFlagTooltip(country)" :iso="country.alpha_2 | lowercase" :squared="false" class="bd-radius" /></span>
        </div>
      </template>

      <!-- website -->
      <template v-if="website || organisation.pipeline_url || linkedInSite || investorFundsData">
        <hr/>
        <span v-if="website" class="mb-1">
          <i class="fa fa-link t-secondary t-sm width--20px mr-1" />
          <a :href="website.url | formatWebsite" target="_blank">Website</a>
        </span>
        <template v-if="investorFundsData">
          <span v-if="investorFundsData.investment_team_url" class="mb-1">
            <i class="fa fa-link t-secondary t-sm width--20px mr-1" />
            <a :href="investorFundsData.investment_team_url | formatWebsite" target="_blank">Investment Team</a>
          </span>
          <span v-if="investorFundsData.investor_news_url" class="mb-1">
            <i class="fa fa-link t-secondary t-sm width--20px mr-1" />
            <a :href="investorFundsData.investor_news_url | formatWebsite" target="_blank">News</a>
          </span>
          <span v-if="investorFundsData.blog_url" class="mb-1">
            <i class="fa fa-link t-secondary t-sm width--20px mr-1" />
            <a :href="investorFundsData.blog_url | formatWebsite" target="_blank">Blog</a>
          </span>
          <span v-if="investorFundsData.founding_news_url" class="mb-1">
            <i class="fa fa-link t-secondary t-sm width--20px mr-1" />
            <a :href="investorFundsData.founding_news_url | formatWebsite" target="_blank">Fund Announcement</a>
          </span>
        </template>
        <span v-if="organisation.pipeline_url" class="mb-1">
          <i class="fa fa-link t-secondary t-sm width--20px mr-1" />
          <a :href="organisation.pipeline_url | formatWebsite" target="_blank">Pipeline Page</a>
        </span>
        <span v-if="linkedInSite" class="mb-1">
          <i class="fa fa-linkedin t-secondary t-sm width--20px mr-1" />
          <a :href="linkedInSite.url | formatWebsite" target="_blank">LinkedIn Page</a>
        </span>
      </template>
    </template>

    <!-- render spinner if not loaded -->
    <spinner v-if="!isLoaded" :containerClass="`width--50px min-height--150px`" />
  </div>
</template>

<script>
import organisationMixins from '@/mixins/organisation'
import Spinner from '@/components/shared/Spinner'
import RelatedOrganisations from '@/components/organisation/show/RelatedOrganisations'

export default {
  name: 'organisationInformation',
  mixins: [organisationMixins],
  components: {
    Spinner,
    RelatedOrganisations
  },
  props: {
    isLoaded: {
      type: Boolean,
      required: true
    },
    organisation: {
      type: Object,
      required: true
    }
  },
  computed: {
    foundationYear () {
      if (!this.organisation.foundation_date) return ''
      return new Date(this.organisation.foundation_date).getFullYear()
    },
    website () {
      if (this.isEmpty(this.organisation.urls)) return null
      return this.organisation.urls.find(organisation => {
        return organisation.note === 'PD'
      })
    },
    linkedInSite () {
      if (this.isEmpty(this.organisation.urls)) return null
      // Check organisation website, try to find one with note 'corporate_linked_in_url'
      return this.organisation.urls.find(organisation => {
        return organisation.note === 'LI'
      })
    },
    principalLeaders () {
      if (this.isInvestor || this.isEmpty(this.organisation.leaderships)) return []
      return this.organisation.leaderships.filter(leader => {
        return leader.founder
      })
    },
    isAI () {
      return this.organisation.foundation_date_is_ai
    }
  }
}
</script>
